/**
 * The task table, grouped by verb (sketch shape A). Inside a group, rows
 * sort by runs then map order; groups follow the API's verb order. The
 * unsorted rows (no tool call, no matching task, could not read) follow as
 * one group, and the tools no task uses close the table as one more, only
 * when there are some. Each group name and each counted column carries an
 * info icon with a one-sentence tooltip. The table sits in a hairline box
 * with floating side-scroll arrows, like the Request log, so a mouse user
 * can reach the right columns on a narrow screen.
 *
 * One font: the body font in normal case everywhere, tool names in the
 * code font (ToolChip). The "Not called" tag shows only once the window
 * holds enough counted runs (marksUnused). A task carries it when none of
 * its tools was called (isIdleJob), in place of its share bar unless the
 * window still holds runs under its id (ShareCell); the unsorted rows
 * have no tools and never do.
 */

import { useRef } from 'react'

import type { JobMapJob, JobMapReport, JobMapTool } from '@/api/generated/model'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

import { InfoTip } from '@/components/shared/InfoTip'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import {
  BROKEN_TOOLS_NOTE,
  brokenToolRow,
  COLUMN_NOTES,
  formatCount,
  formatShare,
  groupJobs,
  isIdleJob,
  NO_TASK_TOOLS_LABEL,
  NO_TASK_TOOLS_NOTE,
  pluralize,
  toolsInNoTask,
  unusedNote,
  unusedToolNames,
  type JobGroup,
} from './jobGroups'
import { NotCalledTag } from './NotCalledTag'
import { ToolChip } from './ToolChips'

const STEEL_BLUE = 'hsl(var(--chart-1))'
const COLUMNS = 5
/** Over the table's default header style (capitals in the code font). */
const HEAD = 'font-sans normal-case tracking-normal'
const NUMBER = 'text-right text-xs tabular-nums'

export function JobMapTable({ report }: { report: JobMapReport }): React.JSX.Element {
  const unused = unusedToolNames(report)
  const note = unusedNote(report)
  const extraTools = toolsInNoTask(report)
  const broken = brokenToolRow(report)
  const scrollRef = useRef<HTMLDivElement>(null)
  return (
    // `relative` anchors the floating scroll arrows to this box.
    <div className="relative border border-border">
      <SideScrollArrows scrollRef={scrollRef} />
      <Table containerRef={scrollRef}>
        <TableHeader>
          <TableRow>
            <TableHead className={HEAD}>Task</TableHead>
            <TableHead className={HEAD}>
              <ColumnLabel name="Tools" />
            </TableHead>
            <TableHead className={cn(HEAD, 'w-48')}>
              <ColumnLabel name="Share" />
            </TableHead>
            <TableHead className={cn(HEAD, 'w-20 text-right')}>
              <ColumnLabel name="Runs" />
            </TableHead>
            <TableHead className={cn(HEAD, 'w-20 text-right')}>
              <ColumnLabel name="Flags" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupJobs(report).map((group) => (
            <GroupRows
              key={group.label}
              group={group}
              unused={unused}
              note={note}
            />
          ))}
          {(extraTools.length > 0 || broken !== null) && (
            <ToolGroupRows tools={extraTools} broken={broken} />
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function ColumnLabel({ name }: { name: keyof typeof COLUMN_NOTES }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1">
      {name}
      <InfoTip text={COLUMN_NOTES[name]} />
    </span>
  )
}

interface GroupHeaderProps {
  label: string
  description: string | null
  meta: string
}

function GroupHeader({ label, description, meta }: GroupHeaderProps): React.JSX.Element {
  return (
    <TableRow className="bg-muted/40 hover:bg-muted/40" data-group={label}>
      <TableCell colSpan={COLUMNS} className="py-2">
        <span className="inline-flex items-center gap-1.5 font-sans text-sm font-medium">
          {label}
          {description !== null && <InfoTip text={description} />}
        </span>
        <span className="ml-3 font-sans text-xs text-muted-foreground">{meta}</span>
      </TableCell>
    </TableRow>
  )
}

interface GroupRowsProps {
  group: JobGroup
  unused: Set<string>
  note: string
}

function GroupRows({ group, unused, note }: GroupRowsProps): React.JSX.Element {
  const meta =
    pluralize(group.jobs.length, 'task') +
    (group.share === null ? '' : ` · ${formatShare(group.share)}`)
  return (
    <>
      <GroupHeader label={group.label} description={group.description} meta={meta} />
      {group.jobs.map((job) => (
        <JobRow key={job.id} job={job} unused={unused} note={note} />
      ))}
    </>
  )
}

interface JobRowProps {
  job: JobMapJob
  unused: Set<string>
  note: string
}

function JobRow({ job, unused, note }: JobRowProps): React.JSX.Element {
  const idle = isIdleJob(job, unused)
  return (
    <TableRow data-job={job.id} data-unused={idle ? '' : undefined}>
      <TableCell className="text-sm">{job.name}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {job.tools.map((tool) => (
            <ToolChip key={tool} name={tool} />
          ))}
        </div>
      </TableCell>
      <TableCell>
        <ShareCell job={job} idle={idle} note={note} />
      </TableCell>
      <TableCell className={NUMBER}>{formatCount(job.runs)}</TableCell>
      <TableCell className={NUMBER}>{formatCount(job.flags)}</TableCell>
    </TableRow>
  )
}

interface ShareCellProps {
  job: JobMapJob
  idle: boolean
  note: string
}

/**
 * The share bar, the "Not called" tag, or both. An idle task normally has
 * no runs and shows the tag alone. A task keeps the runs recorded under
 * its id for the window even after its tools were changed in the map, so
 * an idle task with runs keeps its bar and shows the tag beside it.
 */
function ShareCell({ job, idle, note }: ShareCellProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      {(!idle || job.runs > 0) && <ShareBar share={job.share} />}
      {idle && <NotCalledTag note={note} />}
    </div>
  )
}

function ShareBar({ share }: { share: number }): React.JSX.Element {
  return (
    <div className="flex items-center gap-2" data-share-bar="">
      <div className="h-1.5 w-24 bg-muted" aria-hidden="true">
        <div
          className="h-full"
          style={{ width: `${String(Math.min(100, share * 100))}%`, backgroundColor: STEEL_BLUE }}
        />
      </div>
      <span className="text-xs tabular-nums">{formatShare(share)}</span>
    </div>
  )
}

interface ToolGroupRowsProps {
  tools: JobMapTool[]
  /** The summed row of calls the agent wrote wrong; null when there were none. */
  broken: JobMapTool | null
}

/**
 * The tools no task uses: one row per tool, with the runs that called it,
 * and last the one row for the calls the agent wrote wrong.
 */
function ToolGroupRows({ tools, broken }: ToolGroupRowsProps): React.JSX.Element {
  return (
    <>
      <GroupHeader
        label={NO_TASK_TOOLS_LABEL}
        description={NO_TASK_TOOLS_NOTE}
        meta={pluralize(tools.length, 'tool')}
      />
      {tools.map((tool) => (
        <ToolRow key={tool.name} tool={tool}>
          <ToolChip name={tool.name} />
        </ToolRow>
      ))}
      {broken !== null && (
        <ToolRow tool={broken}>
          <span className="inline-flex items-center gap-1 text-sm">
            {broken.name}
            <InfoTip text={BROKEN_TOOLS_NOTE} />
          </span>
        </ToolRow>
      )}
    </>
  )
}

function ToolRow({ tool, children }: { tool: JobMapTool; children: React.ReactNode }): React.JSX.Element {
  return (
    <TableRow data-tool={tool.name}>
      <TableCell>{children}</TableCell>
      <TableCell />
      <TableCell />
      <TableCell className={NUMBER}>{formatCount(tool.runs)}</TableCell>
      <TableCell />
    </TableRow>
  )
}
